import React, { useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { Award, BookOpen, Eye, Zap, ChevronDown, ChevronUp } from 'lucide-react';
import { VideoPlayer } from './VideoPlayer';
import './VideoLessons.css';

// ─────────────────────────────────────────────────────────────
// VIDEO DATA — sourced from public educational YouTube channels.
// Each video is mapped to its CBR curriculum category.
//
// NOTE ON BRANDING: YouTube embed params (modestbranding=1,
// rel=0) reduce but cannot fully remove YouTube UI during
// playback — this is required by YouTube ToS. Long-term plan:
// replace these with AI-generated self-hosted videos so no
// third-party branding appears at all.
// ─────────────────────────────────────────────────────────────

export interface VideoItem {
  id: string;
  youtubeId: string;
  titleNl: string;
  titleEn: string;
  titleAr: string;
  channel: string;
  isOfficial?: boolean;       // CBR official source
  cbrTopicIds: string[];      // maps to categoryStructure topic IDs
  durationMin: number;
}

export interface VideoSection {
  id: string;
  labelKey: string;
  labelNl: string;
  labelEn: string;
  labelAr: string;
  descriptionNl: string;
  descriptionEn: string;
  descriptionAr: string;
  icon: React.ComponentType<{ className?: string; size?: number }>;
  accentColor: string;
  videos: VideoItem[];
  isPremium?: boolean;
}

// ─── Curriculum-accurate video library ───────────────────────
// Only our 4 AI-produced self-hosted videos + the official CBR example.
// All other YouTube embeds removed — users can find those on YouTube.

export const VIDEO_SECTIONS: VideoSection[] = [
  {
    id: 'animation-questions',
    labelKey: 'videos.sections.animationQuestions',
    labelNl: 'Animatievragen 2025/2026',
    labelEn: 'Animation Questions 2025/2026',
    labelAr: 'أسئلة الرسوم المتحركة 2025/2026',
    descriptionNl:
      'Het CBR examen bevat nu bewegende beeld vragen (animaties). Dit is de grootste verandering. Bekijk hoe ze eruitzien en hoe je ze beantwoordt.',
    descriptionEn:
      'The CBR exam now includes animated video questions (bewegende beeld vragen). This is the biggest exam change. Watch how they look and how to answer them correctly.',
    descriptionAr:
      'يتضمن امتحان CBR الآن أسئلة مصورة متحركة. هذا هو أكبر تغيير في الامتحان. شاهد كيف تبدو وكيف تجيب عليها.',
    icon: Zap,
    accentColor: '#1A3E7A',
    videos: [
      {
        id: 'cbr-official-animation',
        youtubeId: 'o_R09WL1jhc',
        titleNl: 'Officieel CBR Voorbeeld – Bewegende Beeld Vraag',
        titleEn: 'Official CBR Example – Animation Question Format',
        titleAr: 'مثال CBR الرسمي – صيغة سؤال متحرك',
        channel: 'CBR Nederland (officieel)',
        isOfficial: true,
        cbrTopicIds: ['hazardPerception', 'priorityRules'],
        durationMin: 1,
      },
    ],
  },
  {
    id: 'priority-rules',
    labelKey: 'videos.sections.priorityRules',
    labelNl: 'Voorrangsregels',
    labelEn: 'Right of Way Rules',
    labelAr: 'قواعد الأولوية في الطريق',
    descriptionNl:
      'Voorrangsregels zijn verantwoordelijk voor de meeste fouten op het CBR theorie-examen. Leer de basisregels, kruispunten en voetgangers correct.',
    descriptionEn:
      'Right-of-way rules cause the most mistakes on the CBR theory exam. Master junctions, roundabouts and pedestrian priority.',
    descriptionAr:
      'قواعد الأولوية هي المسؤولة عن أكثر الأخطاء في اختبار CBR النظري. تعلم قواعد التقاطعات ودوارات المرور والمشاة.',
    icon: BookOpen,
    accentColor: '#059669',
    videos: [
      {
        id: 'theorietoppers-voorrang-basics',
        youtubeId: 'UBZEsEgQ95w',
        titleNl: 'Basisregels Voorrang – Wat Je MOET Weten',
        titleEn: 'Basic Priority Rules – What You MUST Know',
        titleAr: 'قواعد الأولوية الأساسية – ما يجب أن تعرفه',
        channel: 'TheoryCoach',
        isOfficial: false,
        cbrTopicIds: ['priorityRules', 'expandedPriorityRules'],
        durationMin: 10,
      },
    ],
  },
  {
    id: 'hazard-perception',
    labelKey: 'videos.sections.hazardPerception',
    labelNl: 'Gevaarherkenning',
    labelEn: 'Hazard Perception',
    labelAr: 'إدراك المخاطر',
    descriptionNl:
      'Gevaarherkenning toetst of je gevaren op de weg tijdig signaleert. Leer workarounds en patronen die terugkomen in het CBR examen.',
    descriptionEn:
      'Hazard perception tests whether you can spot road dangers early. Learn the patterns that repeatedly appear in CBR exams.',
    descriptionAr:
      'يختبر إدراك المخاطر قدرتك على رصد مخاطر الطريق مبكراً. تعلم الأنماط التي تتكرر في امتحانات CBR.',
    icon: Eye,
    accentColor: '#D97706',
    videos: [
      {
        id: 'gevaarherkenning-tips',
        youtubeId: 'iANGzs1diS0',
        titleNl: 'Gevaarherkenning – Tips & Tricks voor CBR',
        titleEn: 'Hazard Perception – Tips & Tricks for CBR',
        titleAr: 'إدراك المخاطر – نصائح وحيل لامتحان CBR',
        channel: 'TheoryCoach',
        isOfficial: false,
        cbrTopicIds: ['hazardPerception', 'riskAwareness'],
        durationMin: 14,
      },
    ],
  },
  {
    id: 'vehicle-knowledge',
    labelKey: 'videos.sections.vehicleKnowledge',
    labelNl: 'Voertuigkennis & Verlichting',
    labelEn: 'Vehicle Knowledge & Lights',
    labelAr: 'معرفة السيارة والإضاءة',
    descriptionNl:
      'Verlichting is een veelgesteld onderwerp op het CBR examen. Weet wanneer je welke lichten gebruikt.',
    descriptionEn:
      'Vehicle lighting is a common CBR exam topic. Know exactly when to use each type of light.',
    descriptionAr:
      'إضاءة السيارة موضوع شائع في امتحان CBR. اعرف متى تستخدم كل نوع من الأضواء.',
    icon: Award,
    accentColor: '#7C3AED',
    videos: [
      {
        id: 'theorietoppers-verlichting',
        youtubeId: '71XMMI4BIdY',
        titleNl: 'Gebruik van Lichten – Alles voor het Theorie Examen',
        titleEn: 'Use of Lights – Everything for the Theory Exam',
        titleAr: 'استخدام الأضواء – كل ما تحتاجه لاختبار النظرية',
        channel: 'TheoryCoach',
        isOfficial: false,
        cbrTopicIds: ['vehicleHandling', 'nightVisibility'],
        durationMin: 9,
      },
    ],
  },
];

// ─── Component ───────────────────────────────────────────────

const VideoCard: React.FC<{ video: VideoItem; lang: string; sectionId: string }> = ({
  video,
  lang,
  sectionId,
}) => {
  const title =
    lang === 'ar' ? video.titleAr : lang === 'nl' ? video.titleNl : video.titleEn;
  const validLang: 'nl' | 'en' | 'ar' = lang === 'nl' || lang === 'ar' ? lang : 'en';

  return (
    <div className="vl-card">
      {/* VideoPlayer: self-hosted MP4 first, YouTube fallback if not uploaded yet */}
      <VideoPlayer
        sectionId={sectionId}
        videoId={video.id}
        youtubeId={video.youtubeId}
        title={title}
        durationMin={video.durationMin}
        lang={validLang}
      />
      <div className="vl-card-body">
        <div className="vl-card-meta">
          {video.isOfficial && (
            <span className="vl-badge vl-badge--official">
              <Award size={10} />
              CBR Officieel
            </span>
          )}
          <span className="vl-channel">{video.channel}</span>
        </div>
        <p className="vl-card-title">{title}</p>
      </div>
    </div>
  );
};

const SectionBlock: React.FC<{
  section: VideoSection;
  lang: string;
  defaultOpen?: boolean;
}> = ({ section, lang, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);

  const label =
    lang === 'ar' ? section.labelAr : lang === 'nl' ? section.labelNl : section.labelEn;
  const description =
    lang === 'ar'
      ? section.descriptionAr
      : lang === 'nl'
      ? section.descriptionNl
      : section.descriptionEn;

  const IconComponent = section.icon;

  return (
    <div className={`vl-section ${open ? 'vl-section--open' : ''}`}>
      <button
        className="vl-section-header"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        style={{ '--section-accent': section.accentColor } as React.CSSProperties}
      >
        <div className="vl-section-header-left">
          <div
            className="vl-section-icon-wrap"
            style={{ background: `${section.accentColor}18` }}
          >
            <span style={{ color: section.accentColor, display: 'flex' }}>
              <IconComponent
                className="vl-section-icon"
                size={20}
              />
            </span>
          </div>
          <div className="vl-section-title-group">
            <span className="vl-section-label">{label}</span>
            <span className="vl-section-count">{section.videos.length} video's</span>
          </div>
        </div>
        <div className="vl-section-header-right">
          {section.isPremium && <span className="vl-badge vl-badge--pro">Pro</span>}
          {open ? <ChevronUp size={18} className="vl-chevron" /> : <ChevronDown size={18} className="vl-chevron" />}
        </div>
      </button>

      {open && (
        <div className="vl-section-body">
          <p className="vl-section-description">{description}</p>
          <div className="vl-cards-grid">
            {section.videos.map((video) => (
              <VideoCard key={video.id} video={video} lang={lang} sectionId={section.id} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export const VideoLessons: React.FC = () => {
  const { currentLanguage } = useLanguage();
  const lang = currentLanguage || 'en';

  const heading =
    lang === 'ar'
      ? 'دروس الفيديو'
      : lang === 'nl'
      ? 'Videolessen'
      : 'Video Lessons';

  const subheading =
    lang === 'ar'
      ? 'استعد لامتحان CBR 2025/2026 بما في ذلك أسئلة الرسوم المتحركة الجديدة'
      : lang === 'nl'
      ? 'Bereid je voor op het CBR examen 2025/2026, inclusief de nieuwe animatievragen'
      : 'Prepare for the CBR exam 2025/2026, including the new animation questions';

  return (
    <div className="vl-page">
      <div className="vl-page-header">
        <h1 className="vl-page-title">{heading}</h1>
        <p className="vl-page-subtitle">{subheading}</p>
      </div>

      <div className="vl-sections">
        {VIDEO_SECTIONS.map((section, idx) => (
          <SectionBlock
            key={section.id}
            section={section}
            lang={lang}
            defaultOpen={idx === 0}
          />
        ))}
      </div>
    </div>
  );
};

export default VideoLessons;
